import FormControl from '@mui/material/FormControl';
import {useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {SyncProject} from '@/components/SyncProject';
import {useToken} from '@/components/TokenContext';
import type {AccessibleResources, Project} from '@/types/atlassian';

declare global {
    interface Window {
        FileMaker?: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            PerformScript: (script: string, parameter: string) => void;
            // eslint-disable-next-line @typescript-eslint/naming-convention
            PerformScriptWithOption: (script: string, parameter: string, option: string) => void;
        };
    }
}

const useQuery = () => {
    const {search} = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
};

const Home = (): JSX.Element => {
    const query = useQuery();
    const {token} = useToken();
    const [jiraInstanceId, setJiraInstanceId] = useState<string>('');
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [status, setStatus] = useState('Loading...');
    const webhookId = query.get('webhookId');
    const project = query.get('project');
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const jiraBaseUrl = query.get('jiraBaseUrl') ?? '';
    const notBefore = query.get('notBefore') ?? '';

    const batchSize = useMemo(() => {
        const paramSize = Number(query.get('batchSize'));
        return isNaN(paramSize) || paramSize < 1 ? 1000 : paramSize;
    }, [query]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: you can't make me
    useEffect(() => {
        const getProjects = async () => {
            if (!token) {
                return;
            }

            const resourcesResponse = await fetch(
                'https://api.atlassian.com/oauth/token/accessible-resources',
                {
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                },
            );

            const resources = (await resourcesResponse.json()) as AccessibleResources[];

            const jiraInstance = resources.find(r =>
                jiraBaseUrl.toLowerCase().startsWith(r.url.toLowerCase()),
            );

            if (!jiraInstance) {
                setStatus(
                    `Failed to Find Jira instance ${jiraBaseUrl} in accessible resources` +
                        ` ${resources.map(r => r.url).join(', ')}`,
                );
                return;
            }

            setJiraInstanceId(jiraInstance.id);
            const response = await fetch(
                `https://api.atlassian.com/ex/jira/${jiraInstance.id}/rest/api/3/project`,
                {
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                },
            );

            if (!response.ok) {
                setStatus('Failed to fetch projects.');
            }

            const projects = (await response.json()) as Project[];
            const desiredProject = projects.find(p => p.key === project);

            setSelectedProject(desiredProject ?? null);
            setProjects(projects);
            setStatus(desiredProject ? '' : `Failed to find project ${project ?? ''}`);
        };

        if (token) {
            getProjects().catch(e => {
                setStatus('Failed to fetch projects.');
                console.error(e);
            });
        }
    }, [token]);

    return (
        <>
            {status}
            {projects.length > 0 && (
                <FormControl fullWidth>
                    {selectedProject !== null && (
                        <SyncProject
                            accessToken={token ?? ''}
                            project={selectedProject}
                            webhookId={webhookId ?? ''}
                            jiraInstanceId={jiraInstanceId}
                            batchSize={batchSize}
                            notBefore={notBefore}
                            sendIssuesToFM={(payload: string) => {
                                window.FileMaker
                                    ? window.FileMaker.PerformScript(
                                          'trackerAtlassianApp__processJiraData',
                                          payload,
                                      )
                                    : console.log('no fm', payload);
                            }}
                            sentLastBatch={(payload: string) => {
                                window.FileMaker
                                    ? window.FileMaker.PerformScript(
                                          'trackerAtlassianApp__lastBatchSent',
                                          payload,
                                      )
                                    : console.log('no fm', payload);
                            }}
                        />
                    )}
                </FormControl>
            )}
        </>
    );
};

export default Home;
